import { useCallback, useMemo, type Dispatch, type SetStateAction } from "react"

import type { ColumnDef as UiColumnDef } from "@coval/ui"

import type { ColumnDef } from "../types/columns"

interface UseRunsTableColumnControlsParams<TRun, TSimulation> {
  activeColumns: ColumnDef[]
  baseRunColumns: ColumnDef[]
  baseSimulationColumns: ColumnDef[]
  runColumns: UiColumnDef<TRun>[]
  setVisibleColumns: Dispatch<SetStateAction<Set<string>>>
  simulationColumns: UiColumnDef<TSimulation>[]
  visibleColumns: Set<string>
}

/** Uses the complete rendered column definitions, including individual metrics. */
export function useRunsTableColumnControls<TRun, TSimulation>({
  activeColumns,
  runColumns,
  setVisibleColumns,
  simulationColumns,
  visibleColumns,
}: UseRunsTableColumnControlsParams<TRun, TSimulation>) {
  const columnVisibility = useMemo(
    () =>
      Object.fromEntries(
        activeColumns.map((column) => [
          column.key,
          visibleColumns.has(column.key),
        ]),
      ),
    [activeColumns, visibleColumns],
  )

  const handleColumnVisibilityChange = useCallback(
    (visibility: Record<string, boolean>) => {
      setVisibleColumns((previous) => {
        const next = new Set(previous)
        activeColumns.forEach(({ key }) => {
          if (visibility[key] ?? true) {
            next.add(key)
          } else {
            next.delete(key)
          }
        })
        return next
      })
    },
    [activeColumns, setVisibleColumns],
  )

  return {
    columnVisibility,
    handleColumnVisibilityChange,
    runToggleColumns: runColumns,
    simulationToggleColumns: simulationColumns,
  }
}
